// color-exempt-file — retokenisation 5728fe509 : classes dark:bg-[#hex] ajoutees pour preserver le rendu dark historique (voir memoire ui-themes)
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Calendar, Mail, Check } from "lucide-react";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@core/components/ui/accordion";
import { useAuth } from "@core/contexts";
import { SEOHead } from "../components/SEOHead";
import { HOME_JSON_LD_EN } from "../seo/homeJsonLdEn";
import { isEnPath } from "../seo/enRoutes";
import { formatCurrency } from "@core/lib/date-utils";
import { PLANS } from "@core/constants/quotas";
import { DiscoverLinks } from "../components/PublicFooter";

const RETURNING_VISITOR_KEY = "doodates_has_visited";

// Les 4 guides que liait la landing précédente (décision du 14/10).
const LANDING_GUIDES = [
  { slug: "organiser-soiree-amis", key: "polls:landing.guides.friends_party" },
  {
    slug: "planifier-club-sportif-culturel",
    key: "polls:landing.guides.sports_club",
  },
  { slug: "planifier-reunion-equipe", key: "polls:landing.guides.team_meeting" },
  { slug: "planifier-voyage-groupe", key: "polls:landing.guides.group_trip" },
] as const;

// Dimensions natives des captures /press/*.png (1280×800) : posées en
// attributs width/height pour réserver la place et éviter le CLS.
const SHOT_W = 1280;
const SHOT_H = 800;

/**
 * Cadre d'appareil en CSS pur autour d'une vraie capture d'écran.
 * Le cadre est volontairement neutre (pas de couleur d'accent).
 */
function DeviceFrame({
  src,
  alt,
  lazy = false,
  className = "",
  testId,
}: {
  src: string;
  alt: string;
  lazy?: boolean;
  className?: string;
  testId?: string;
}) {
  return (
    <div
      className={`rounded-[14px] sm:rounded-[20px] bg-neutral-900 p-1 sm:p-2 shadow-xl ring-1 ring-black/10 ${className}`}
      data-testid={testId}
    >
      <img
        src={src}
        alt={alt}
        width={SHOT_W}
        height={SHOT_H}
        loading={lazy ? "lazy" : "eager"}
        decoding="async"
        className="block w-full h-auto rounded-[10px] sm:rounded-[14px] bg-white"
      />
    </div>
  );
}

export default function LandingPage() {
  const { t, i18n } = useTranslation(["polls", "common", "legal"]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const forceHome = searchParams.get("home") === "1";

  useEffect(() => {
    if (loading || forceHome) return;

    if (user) {
      navigate("/dashboard", { replace: true });
      return;
    }

    const hasVisited = localStorage.getItem(RETURNING_VISITOR_KEY);
    if (hasVisited) {
      navigate("/create", { replace: true });
      return;
    }

    localStorage.setItem(RETURNING_VISITOR_KEY, "1");
  }, [user, loading, navigate, forceHome]);

  const { pathname } = useLocation();

  const steps = [
    {
      step: "1",
      title: t("polls:landing.process.step1_title"),
      desc: t("polls:landing.process.step1_desc"),
      img: "/press/1-create.png",
      alt: t("polls:landing.process.step1_alt"),
    },
    {
      step: "2",
      title: t("polls:landing.process.step2_title"),
      desc: t("polls:landing.process.step2_desc"),
      img: "/press/2-vote.png",
      alt: t("polls:landing.process.step2_alt"),
    },
    {
      step: "3",
      title: t("polls:landing.process.step3_title"),
      desc: t("polls:landing.process.step3_desc"),
      img: "/press/3-results.png",
      alt: t("polls:landing.process.step3_alt"),
    },
  ];

  const useCases = [
    {
      key: "club",
      title: t("polls:landing.use_cases.club_title"),
      desc: t("polls:landing.use_cases.club_desc"),
    },
    {
      key: "choir",
      title: t("polls:landing.use_cases.choir_title"),
      desc: t("polls:landing.use_cases.choir_desc"),
    },
    {
      key: "association",
      title: t("polls:landing.use_cases.association_title"),
      desc: t("polls:landing.use_cases.association_desc"),
    },
  ];

  const faq = [
    {
      value: "q1",
      question: t("polls:landing.faq.q1_question"),
      answer: t("polls:landing.faq.q1_answer"),
      link: { label: t("polls:landing.faq.learn_more"), href: "/docs" },
    },
    {
      value: "q2",
      question: t("polls:landing.faq.q2_question"),
      answer: t("polls:landing.faq.q2_answer"),
      link: { label: t("polls:landing.faq.full_guide"), href: "/docs" },
    },
    {
      value: "q3",
      question: t("polls:landing.faq.q3_question"),
      answer: t("polls:landing.faq.q3_answer"),
      link: { label: t("polls:landing.faq.learn_more"), href: "/docs" },
    },
    {
      value: "q4",
      question: t("polls:landing.faq.q4_question"),
      answer: t("polls:landing.faq.q4_answer"),
      link: {
        label: t("polls:landing.faq.privacy_policy"),
        href: "/privacy",
      },
    },
  ];

  const textLink =
    "text-foreground underline underline-offset-4 decoration-muted-foreground/50 hover:decoration-foreground transition-colors";

  return (
    <div className="flex min-h-screen-safe bg-background dark:bg-[#030712] text-foreground">
      <SEOHead
        title={t("polls:landing.meta_title")}
        description={t("polls:landing.meta_description")}
        // FR : JSON-LD statique d'index.html. /en : son équivalent anglais.
        jsonLd={pathname === "/en" ? HOME_JSON_LD_EN : false}
      />
      <ProductSidebar productType="date" />

      <div className="flex-1 min-w-0 w-full">
        <ProductHeader productType="date" showAuth={true} variant="landing" />

        {/* IMPORTANT: NE PAS AJOUTER DE Z-INDEX ICI - Crée des contextes d'empilement qui bloquent le widget de support */}
        <div className="relative overflow-x-hidden">
          {/* Hero */}
          <section className="py-14 sm:py-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="grid items-center gap-12 lg:grid-cols-2">
                <div className="text-center lg:text-left">
                  <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-[1.1]">
                    {t("polls:landing.hero.headline_lead")}{" "}
                    {t("polls:landing.hero.headline_highlight")}
                  </h1>

                  <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto lg:mx-0 mb-10 leading-relaxed">
                    {t("polls:landing.hero.subtitle")}
                  </p>

                  <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 sm:gap-6">
                    <Link
                      to="/create"
                      data-testid="create-date-poll-button"
                      className="px-7 py-3.5 text-base font-medium bg-primary hover:bg-primary/90 rounded-lg transition-colors text-primary-foreground inline-flex"
                    >
                      {t("polls:create_poll")}
                    </Link>
                    <Link
                      to={isEnPath(pathname) ? "/en/ai" : "/ai"}
                      data-testid="hero-ai-link"
                      className={`text-base font-medium ${textLink}`}
                    >
                      {t("polls:landing.hero.ai_link")}
                    </Link>
                  </div>

                  <p className="mt-6 text-sm text-muted-foreground">
                    {t("polls:landing.hero.reassurance")}
                  </p>
                </div>

                {/* Captures réelles : vote au premier plan, résultats derrière */}
                <div
                  className="relative mx-auto w-full max-w-xl lg:max-w-none pb-[22%] pr-[12%]"
                  data-testid="hero-screenshots"
                >
                  <DeviceFrame
                    src="/press/2-vote.png"
                    alt={t("polls:landing.hero.screenshot_vote_alt")}
                  />
                  <DeviceFrame
                    src="/press/3-results.png"
                    alt={t("polls:landing.hero.screenshot_results_alt")}
                    lazy
                    className="absolute bottom-0 right-0 w-[55%]"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* 3 étapes */}
          <section className="py-16 sm:py-24 border-t border-border">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="mb-12 max-w-2xl">
                <h2 className="text-3xl md:text-4xl font-bold mb-3">
                  {t("polls:landing.process.title_lead")}{" "}
                  {t("polls:landing.process.title_highlight")}
                </h2>
                <p className="text-muted-foreground">
                  {t("polls:landing.process.subtitle")}
                </p>
              </div>

              <ol className="grid md:grid-cols-3 gap-10 md:gap-8">
                {steps.map((item) => (
                  <li key={item.step} className="flex flex-col">
                    <DeviceFrame
                      src={item.img}
                      alt={item.alt}
                      lazy
                      className="mb-5"
                    />
                    <h3 className="text-lg font-semibold mb-2">
                      <span className="text-muted-foreground mr-2">
                        {item.step}.
                      </span>
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.desc}
                    </p>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* Usages récurrents */}
          <section
            className="py-16 sm:py-24 border-t border-border"
            data-testid="landing-use-cases"
          >
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="mb-12 max-w-2xl">
                <h2 className="text-3xl md:text-4xl font-bold mb-3">
                  {t("polls:landing.use_cases.title")}
                </h2>
                <p className="text-muted-foreground">
                  {t("polls:landing.use_cases.subtitle")}
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-8">
                {useCases.map((item) => (
                  <div key={item.key} className="border-l-2 border-border pl-5">
                    <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Tarifs — preview (détails complets sur /pricing) */}
          <section className="py-16 sm:py-24 border-t border-border">
            <div className="max-w-5xl mx-auto px-4 sm:px-6">
              <div className="mb-12 max-w-2xl">
                <h2 className="text-3xl md:text-4xl font-bold mb-3">
                  {t("polls:landing.pricing.title_lead")}{" "}
                  {t("polls:landing.pricing.title_highlight")}
                </h2>
                <p className="text-muted-foreground">
                  {t("polls:landing.pricing.subtitle")}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 max-w-3xl">
                {[
                  {
                    key: "free",
                    name: t("polls:landing.pricing.free_name"),
                    price: formatCurrency(0, i18n.language),
                    period: t("polls:landing.pricing.free_period"),
                    feats: [
                      t("polls:landing.pricing.free_feat1"),
                      t("polls:landing.pricing.free_feat2"),
                      t("polls:landing.pricing.free_feat3"),
                    ],
                    cta: t("polls:landing.pricing.free_cta"),
                    href: "/create",
                    primary: true,
                  },
                  {
                    key: "solo",
                    name: t("polls:landing.pricing.solo_name"),
                    price: formatCurrency(PLANS.SOLO.price, i18n.language),
                    period: t("polls:landing.pricing.solo_period"),
                    feats: [
                      t("polls:landing.pricing.solo_feat1"),
                      t("polls:landing.pricing.solo_feat2"),
                      t("polls:landing.pricing.solo_feat3"),
                    ],
                    cta: t("polls:landing.pricing.solo_cta"),
                    href: "/pricing",
                    primary: false,
                  },
                ].map((plan) => (
                  <div
                    key={plan.key}
                    className="flex flex-col p-6 sm:p-8 rounded-xl border border-border bg-card"
                  >
                    <h3 className="text-xl font-semibold mb-3">{plan.name}</h3>
                    <div className="mb-6">
                      <span className="text-4xl font-bold">{plan.price}</span>
                      <span className="text-muted-foreground ml-2 text-sm">
                        {plan.period}
                      </span>
                    </div>
                    <ul className="space-y-3 mb-8 flex-grow text-sm">
                      {plan.feats.map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                          <span className="text-muted-foreground">{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={plan.href}
                      className={
                        plan.primary
                          ? "w-full py-3 px-6 text-center bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-medium transition-colors"
                          : "w-full py-3 px-6 text-center border border-border text-foreground rounded-lg font-medium hover:bg-accent transition-colors"
                      }
                    >
                      {plan.cta}
                    </Link>
                  </div>
                ))}
              </div>

              <p className="text-sm text-muted-foreground mt-8">
                <Link to="/pricing" className={textLink}>
                  {t("polls:landing.pricing.compare_all")}
                </Link>
              </p>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-16 sm:py-24 border-t border-border">
            <div className="max-w-3xl mx-auto px-4 sm:px-6">
              <h2 className="text-3xl md:text-4xl font-bold mb-10">
                {t("polls:landing.faq.title_lead")}{" "}
                {t("polls:landing.faq.title_highlight")}
              </h2>
              <Accordion type="single" collapsible className="space-y-2">
                {faq.map((item) => (
                  <AccordionItem
                    key={item.value}
                    value={item.value}
                    className="group border border-border rounded-lg px-4 sm:px-6 bg-card"
                  >
                    <AccordionTrigger className="text-left hover:no-underline text-foreground">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent
                      forceMount
                      className="text-muted-foreground group-data-[state=closed]:hidden"
                    >
                      <p className="mb-3">{item.answer}</p>
                      <Link to={item.link.href} className={`text-sm ${textLink}`}>
                        {item.link.label}
                      </Link>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          {/* Guides : une ligne discrète de liens directs, sans cartes
              (maillage interne, présent dans le HTML pré-rendu). Le contenu
              existe en FR, EN et ES à la même URL (langue active). */}
          <nav
            data-testid="landing-guides-links"
            aria-label={t("polls:landing.guides.badge")}
            className="py-6 border-t border-border"
          >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-sm text-muted-foreground text-center">
              <span className="font-medium">
                {t("polls:landing.guides.badge")}
              </span>
              {LANDING_GUIDES.map((g) => (
                <Link
                  key={g.slug}
                  to={`/guides/${g.slug}`}
                  className="underline underline-offset-4 decoration-border hover:text-foreground hover:decoration-current transition-colors"
                >
                  {t(g.key)}
                </Link>
              ))}
              <Link
                to="/guides"
                className="hover:text-foreground transition-colors"
              >
                {t("polls:landing.guides.see_all")}
              </Link>
            </div>
          </nav>

          {/* Footer */}
          <footer className="py-8 border-t border-border">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    {t("polls:landing.footer.copyright", {
                      year: new Date().getFullYear(),
                    })}
                  </span>
                </div>
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <a
                    href="mailto:support@doodates.com"
                    className="hover:text-foreground transition-colors"
                  >
                    support@doodates.com
                  </a>
                </div>
                <DiscoverLinks />
                <a
                  href="https://toolradar.com/tools/doodates"
                  target="_blank"
                  rel="noopener"
                  data-testid="footer-toolradar"
                  className="block text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t("polls:landing.footer.toolradar")}
                </a>
                <div className="flex flex-wrap justify-center items-center gap-4 text-xs">
                  <Link
                    to="/legal"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t("legal:legal_notice")}
                  </Link>
                  <span className="text-muted-foreground">·</span>
                  <Link
                    to="/terms"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t("legal:terms_title")}
                  </Link>
                  <span className="text-muted-foreground">·</span>
                  <Link
                    to="/privacy"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t("legal:privacy_policy_title")}
                  </Link>
                  <span className="text-muted-foreground">·</span>
                  <Link
                    to="/docs/ia-policy"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {t("legal:ai_privacy_policy")}
                  </Link>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
